import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { useTableParams } from '../../core/hooks/table-params'
import { IColumn, Table } from '../../core/components/Table'
import { Pagination } from '../../core/components/Pagination'
import Loader from '../../core/components/Loader'
import { SimpleSearchPaginationParams } from '../../core/services/utils'
import { IBlogPost } from '../../core/interfaces'
import { getAllBlogPosts, toggleBlogPostPublished } from '../../services/blog-posts'
import { BlogPostModal } from './BlogPostModal'
import { UUID } from 'node:crypto'

export const BlogPostListPage: React.FC = (): JSX.Element => {
    const { page, setPage, size, setSize, totalElements, setTotalElements, sortBy, setSortBy } =
        useTableParams({ defaultSortBy: 'createdAt' })

    const [loading, setLoading] = useState(false)
    const [posts, setPosts] = useState<IBlogPost[]>([])
    const [isCheck, setIsCheck] = useState<string[]>([])
    const [showModal, setShowModal] = useState(false)
    const [editPostId, setEditPostId] = useState<UUID | undefined>()
    const searchParams = useRef<{ [key: string]: string }>({})
    const timer = useRef<NodeJS.Timeout | null>(null)

    const getData = useCallback(async () => {
        setLoading(true)
        try {
            const params: SimpleSearchPaginationParams = { page, size, sortBy }
            params.text = searchParams.current.title
            const response = await getAllBlogPosts(params)
            setTotalElements(response.data.totalElements)
            setPosts(response.data.content)
            setPage(page + 1)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy])

    useEffect(() => {
        getData()
    }, [getData, page, size, sortBy])

    const handleSortBy = (sort: string) => {
        setSortBy(sort === sortBy ? 'createdAt' : sort)
    }

    const handleSearchParams = useCallback(
        async (col: IColumn, q: string) => {
            if (timer.current) clearTimeout(timer.current)
            searchParams.current = { ...searchParams.current, [col.accessor]: q }
            timer.current = setTimeout(async () => {
                setLoading(true)
                try {
                    const params: SimpleSearchPaginationParams = { page: 0, size, sortBy }
                    params.text = searchParams.current.title
                    const { data, status } = await getAllBlogPosts(params)
                    if (status === 200) {
                        setPosts(data.content)
                        setTotalElements(data.totalElements)
                        setSize(data.size)
                        setPage(data.pageNumber + 1)
                    }
                } catch (err) {
                    console.error(err)
                } finally {
                    setLoading(false)
                }
            }, 2000)
        },
        [page, size, sortBy],
    )

    const handleTogglePublished = async (id: UUID) => {
        try {
            await toggleBlogPostPublished(id)
            await getData()
        } catch (e) {
            console.error(e)
        }
    }

    const formatDate = (ts?: number) => {
        if (!ts) return '-'
        return new Date(ts).toLocaleDateString('ro-RO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    const columns: IColumn[] = [
        { title: 'Title', accessor: 'title', sortable: true, searchable: true },
        { title: 'Slug', accessor: 'slug', sortable: true },
        {
            title: 'Author',
            accessor: 'authorName',
            sortable: false,
        },
        {
            title: 'Categories',
            accessor: '',
            render: (row) => (
                <span>{(row.categoryNames ?? []).join(', ') || '-'}</span>
            ),
        },
        {
            title: 'Published',
            accessor: '',
            render: (row) => (
                <span
                    style={{
                        color: row.published ? '#2e7d32' : '#c62828',
                        fontWeight: 600,
                    }}
                >
                    {row.published ? 'Yes' : 'No'}
                </span>
            ),
        },
        {
            title: 'Private',
            accessor: '',
            render: (row) => <span>{row.isPrivate ? 'Yes' : 'No'}</span>,
        },
        {
            title: 'Created',
            accessor: 'createdAt',
            sortable: true,
            render: (row) => <span>{formatDate(row.createdAt)}</span>,
        },
        {
            title: 'Edit',
            accessor: '',
            render: (row) => (
                <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        setEditPostId(row.id)
                        setShowModal(true)
                    }}
                >
                    Edit
                </div>
            ),
        },
        {
            title: 'Publish',
            accessor: '',
            render: (row) => (
                <Button
                    variant="contained"
                    size="small"
                    color={row.published ? 'warning' : 'success'}
                    onClick={() => handleTogglePublished(row.id)}
                >
                    {row.published ? 'Unpublish' : 'Publish'}
                </Button>
            ),
        },
    ]

    return (
        <div style={{ padding: 20, backgroundColor: '#F8F8F8', position: 'relative' }}>
            {loading && <Loader />}
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 20,
                }}
            >
                <h2>Blog Posts</h2>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                        setEditPostId(undefined)
                        setShowModal(true)
                    }}
                >
                    <i className="bi bi-plus-circle me-2 d-flex" />
                    New Post
                </Button>
            </div>
            <div className="overflow-x-auto">
                <Table
                    handleSearch={handleSearchParams}
                    isCheck={isCheck}
                    columns={columns}
                    handleSortBy={handleSortBy}
                    data={posts}
                />
            </div>
            <Pagination
                size={size}
                totalElements={totalElements}
                setSize={setSize}
                page={page + 1}
                changePage={(newPage) => setPage(newPage)}
            />
            <BlogPostModal
                show={showModal}
                onHide={() => setShowModal(false)}
                editPostId={editPostId}
                onSaved={getData}
            />
        </div>
    )
}
